import React, { Dispatch, ReactNode, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import {
  CircledCheckIcon,
  CircledExclamationPointIcon,
  PlusIcon,
  SyncIcon,
  ResetIcon,
  XIcon,
} from '@components/Icons';
import { IntegrationOptionProps, unsupportedInMAS } from '../utils';
import { AccountIconLarge, AccountIconSmall, ConnectedAccountIcon } from '../AccountIcon';
import styles from './Integration.module.scss';
import DropdownMenu from '@components/Menus/DropdownMenu';
import Integration, { FriendType } from '@models/Integration';
import Tooltip, { Overlay } from '@components/Tooltip';
import { localizeNumber } from '@support/number';
import { ClientEnv, DropdownMenuOption } from '@app/types';
import { itemListToString, pluralize } from '@support/string';
import { useDispatch } from 'react-redux';
import { fetchAllIntegrations, syncIntegration } from '@actions/integrations';
import { useReduxActionPolling } from '@support/hooks/use-redux-action-polling';
import { selectIntegrationByID } from '@reducers/user/integrations';
import { SVGProps } from '@components/Icons/SVGWrapper';
import { useTheme } from '@support/hooks/use-theme';

export function AddIntegration({
  className,
  provider,
  label,
  onClick,
  altIcon,
  isInOnboarding,
}: IntegrationOptionProps) {
  return (
    <div
      className={cn(
        className,
        styles.wrapper,
        'integration-option add-integration-option',
        isInOnboarding && styles.onboarding,
      )}
      onClick={onClick}
    >
      {isInOnboarding ? (
        <AccountIconLarge providerOrType={provider} />
      ) : (
        <AccountIconSmall provider={provider} />
      )}
      <span className="integration-option-label">{label}</span>
      {altIcon ? (
        altIcon
      ) : (
        <PlusIcon
          width="15"
          viewBox="0 0 15 15"
          className={cn(styles.plusIcon, 'integration-option-action-icon')}
        />
      )}
    </div>
  );
}

const ConnectedIntegrationIcon = ({
  provider,
  integrationType,
  isInOnboarding,
}: Pick<IntegrationOptionProps, 'provider' | 'integrationType' | 'isInOnboarding'>) => {
  if (!isInOnboarding) {
    return <AccountIconSmall type={integrationType} provider={provider} />;
  }

  return <AccountIconLarge providerOrType={provider} />;
};

const DropdownMenuXIcon = (props) => <XIcon {...props} viewBox="-4 -4 23 23" />;

const friendTypeDisplay = (friendType?: FriendType) => {
  switch (friendType) {
    case 'following':
      return "Importing people you're following";
    case 'followers':
      return 'Importing people who follow you';
    case 'all':
      return 'Importing everyone';
    case 'mutual':
      return 'Importing mutual follows';
  }
};

const StatusOKOverlay = ({ integration }: { integration: Integration; error?: string }) => (
  <Overlay className={styles.tooltipOverlay}>
    {integration.lastSync && (
      <>
        Synced{' '}
        {integration.lastSync.differenceInSeconds() < 60
          ? 'just now'
          : integration.lastSync.relativeDuration}
        <br />
      </>
    )}
    {integration.status?.total && integration.provider !== 'apple-phone-log' ? (
      <>
        {localizeNumber(integration.status.total)}{' '}
        {pluralize(integration.status.total, 'connection')}
        <br />
      </>
    ) : null}
    {integration.metadata.friend_type && <>{friendTypeDisplay(integration.metadata.friend_type)}</>}
  </Overlay>
);

const StatusErrorOverlay = ({
  integration,
  error,
}: {
  integration: Integration;
  error?: string;
}) => {
  return <Overlay className={styles.tooltipOverlay}>{error || integration.status.message}</Overlay>;
};

const messageForError = (error: string) => {
  if (error === 'requires-contacts-access') {
    return 'Allow Clay to access Contacts from System Settings';
  }

  return undefined;
};

export const IntegrationStatus = ({
  integration,
  syncing,
  className,
  error,
  disconnected,
  reconnect,
}: {
  integration: Integration;
  syncing: boolean;
  className: string;
  error?: { message: string; type: string };
  disconnected?: boolean;
  reconnect?: () => void;
}) => {
  const [justFinishedSync, setJustFinishedSync] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const hasError = !!error || integration.status.status === 'error' || disconnected;
  const Icon = hasError ? CircledExclamationPointIcon : CircledCheckIcon;
  const StatusOverlay = hasError ? StatusErrorOverlay : StatusOKOverlay;

  let errorText = undefined;

  if (disconnected) {
    errorText = 'Click to connect';
  }

  if (hasError && !disconnected) {
    errorText = messageForError(error?.type) || error?.message || integration.status.message;
  }

  useEffect(() => {
    if (integration?.lastSync && integration?.lastSync.differenceInSeconds() < 5) {
      setJustFinishedSync(true);
      timerRef.current = setTimeout(() => {
        setJustFinishedSync(false);
      }, 3000);
    }
  }, [integration?.lastSync]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  if (syncing) {
    return (
      <SyncIcon width={15} height={15} className={styles.statusSyncingIcon} color="textSecondary" />
    );
  }

  return (
    <div
      className={cn(
        styles.tooltipIconWrapper,
        className,
        hasError && styles.error,
        justFinishedSync && styles.justFinishedSync,
      )}
      onClick={disconnected ? reconnect : undefined}
    >
      <Tooltip
        placement="right"
        overlay={<StatusOverlay integration={integration} error={errorText} />}
      >
        <Icon width={16} height={16} hoverOpacity hover />
      </Tooltip>
    </div>
  );
};

const WrappedSyncIcon = (props: SVGProps) => (
  <div className={styles.syncIconWrapper}>
    <SyncIcon {...props} />
  </div>
);

interface ConnectIntegrationProps {
  className?: string;
  integration: Integration;
  // label?: ReactNode;
  removeIntegration: () => void;
  isInOnboarding?: boolean;
  // syncIntegration: () => void;
}

export const IntegrationActionsMenu = ({
  integration,
  syncing,
  removeIntegration,
  syncIntegration,
  resetIntegration,
  manageFrom,
  toggleMenu,
  noSyncing,
  additionalOptions = [],
}: {
  integration?: Integration;
  syncing?: boolean;
  removeIntegration?: () => void;
  syncIntegration?: () => void;
  resetIntegration?: () => void;
  manageFrom?: ClientEnv[];
  toggleMenu: Dispatch<boolean>;
  noSyncing?: boolean;
  additionalOptions?: DropdownMenuOption[];
}) => {
  const [theme] = useTheme();
  let syncTitle: ReactNode = 'Sync Again';
  let syncDisabled = false;
  const hideSyncOption =
    noSyncing ||
    (window.desktopIntegrations?.appInfo?.isMAS && unsupportedInMAS(integration?.provider));

  if (!!manageFrom?.length) {
    syncTitle = <>Sync from {itemListToString(manageFrom, 'or')}</>;
  }

  // if recently synced...
  if (integration?.lastSync && integration?.lastSync.differenceInSeconds() < 15) {
    syncTitle = 'All good!';
    syncDisabled = true;
  }

  // if syncing...
  if (syncing) {
    syncTitle = 'Syncing…';
    syncDisabled = true;
  }

  return (
    <DropdownMenu
      triggerVariant="integrationActions"
      triggerClassName={cn(
        'integration-option-action-icon',
        'integration-action-menu-trigger',
        styles.dropdownTrigger,
      )}
      menuClassName={cn(styles.dropdownMenu, styles[theme])}
      options={[
        !hideSyncOption && {
          title: syncTitle,
          icon: WrappedSyncIcon,
          action: syncIntegration,
          disabled: !!manageFrom?.length || syncDisabled,
          className: cn(syncing && styles.syncing),
        },
        !hideSyncOption &&
          resetIntegration && {
            title: 'Erase & Reimport',
            icon: ResetIcon,
            action: resetIntegration,
          },
        ...additionalOptions,
        {
          title: 'Disconnect',
          icon: DropdownMenuXIcon,
          action: removeIntegration,
        },
      ].filter(Boolean)}
      onOpen={() => toggleMenu(true)}
      onClose={() => toggleMenu(false)}
    />
  );
};

export function ConnectedIntegration(props: ConnectIntegrationProps) {
  const dispatch = useDispatch();
  const [menuOpen, setMenuOpen] = useState(false);

  useReduxActionPolling({
    selectShouldStartPolling: (state) =>
      selectIntegrationByID(props.integration.id)(state)?.status?.is_syncing,
    selectShouldEndPolling: (state) =>
      !selectIntegrationByID(props.integration.id)(state)?.status?.is_syncing,
    pollAction: fetchAllIntegrations,
  });

  const sync = () => {
    dispatch(syncIntegration(props.integration));
  };

  let label: ReactNode = props.integration.label;

  if (
    props.isInOnboarding &&
    !!props.integration.type &&
    ['google', 'microsoft'].includes(props.integration.provider)
  ) {
    label = (
      <>
        {props.integration.displayName}{' '}
        <span className="information" style={{ fontSize: '9px' }}>
          {props.integration.type}
        </span>
      </>
    );
  }

  if (props.integration?.provider === 'twitter') {
    label = 'Twitter (legacy)';
  }

  let IconWrapper: ({ children }: { children: any }) => JSX.Element = React.Fragment;
  if (props.isInOnboarding) {
    IconWrapper = ConnectedAccountIcon;
  }

  return (
    <div
      className={cn(
        props.className,
        styles.wrapper,
        'integration-option remove-integration-option',
        props.isInOnboarding && styles.onboarding,
        menuOpen && styles.menuOpen,
      )}
      onClick={props.isInOnboarding ? props.removeIntegration : undefined}
    >
      <IconWrapper>
        <ConnectedIntegrationIcon
          provider={props.integration.provider}
          integrationType={props.integration.type}
          isInOnboarding={props.isInOnboarding}
        />
      </IconWrapper>
      <span className={cn('integration-option-label')}>
        <span className="integration-option-wrapped-label">{label}</span>

        {!props.isInOnboarding && (
          <IntegrationStatus
            className={styles.integrationStatus}
            integration={props.integration}
            syncing={props.integration?.status.is_syncing}
          />
        )}
      </span>

      {!props.isInOnboarding && (
        <IntegrationActionsMenu
          integration={props.integration}
          syncing={props.integration?.status.is_syncing}
          removeIntegration={props.removeIntegration}
          syncIntegration={sync}
          toggleMenu={setMenuOpen}
        />
      )}

      {props.isInOnboarding && (
        <XIcon className="integration-option-action-icon" width={12} height={12} />
      )}
    </div>
  );
}
